import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
  StateOperator,
} from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import {
  DataEntriesControllerService,
  DataEntry,
  TimelineControllerService,
} from '@osos/shared/api';
import {
  DataEntryPresenterModel,
  DataEntryStateEnum,
  DEFAULT_PAGESIZE,
  LoadingIndicator,
  LoadingType,
  ManagedHttpContext,
  Page,
  RefProperty,
  TIMELINE_CATEGORY_ENTITY_ID,
  TimelineCategoryPresenterModel,
  TimelinePostPresenterModel,
  Where,
} from '@osos/shared/types';
import { forkJoin, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { DataEntryFactoryService } from '../../factories';
import { TimelineCategoryFactoryService } from '../../factories/timeline-category-factory/timeline-category-factory.service';
import { ApiUtilsService } from '../../services';
import { LoadingActions } from '../loading/loading.actions';
import { TimelineActions } from './timeline.action';

export interface PostActionLoader {
  isPublishLoading?: boolean;
  isPublishDateLoading?: boolean;
  isPinLoading?: boolean;
}

interface PostActionLoaderItem {
  [key: string]: PostActionLoader;
}
interface TimelineAppliedFilter {
  searchTerm: string;
  skip: number;
  limit: number;
  entityIds: string | string[];
  moduleIds: string | string[];
  state: DataEntryStateEnum | undefined;
  replace?: boolean;
  postIds?: string[];
  displayPinnedPosts?: boolean;
  orderByPublishPosts?: boolean;
  template?: RefProperty;
}

export interface TimelineStateModel {
  /**
   * temporary state
   */
  transitoryTimelineCategories: TimelineCategoryPresenterModel[];
  page: Page<DataEntry>;
  presentedPosts?: TimelinePostPresenterModel[];
  appliedFilter: TimelineAppliedFilter;
  activeCount?: number;
  transitoryPosts: TimelinePostPresenterModel[];
  transitoryTotalCount: number;
  loading: LoadingIndicator;
  selectedPost?: TimelinePostPresenterModel;
  postActionsLoaders: PostActionLoaderItem;
}

@State<TimelineStateModel>({
  name: 'coreTimeline',
  defaults: {
    transitoryTimelineCategories: [],
    page: {
      items: [],
      totalCount: 0,
    },
    presentedPosts: [],
    appliedFilter: {
      searchTerm: '',
      skip: 0,
      entityIds: null,
      moduleIds: null,
      limit: DEFAULT_PAGESIZE,
      state: DataEntryStateEnum.Active,
      template: null,
    },
    loading: new LoadingIndicator(),
    transitoryPosts: [],
    transitoryTotalCount: 0,
    selectedPost: null,
    postActionsLoaders: {},
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class TimelineState {
  constructor(
    private apiHelper: ApiUtilsService,
    // TODO: We have TimelinePostFactoryService, but when we request this parser, dataEntryParser returned, check
    // when work on scheduled
    private dataEntryFactoryService: DataEntryFactoryService,
    private timelineService: TimelineControllerService,
    private timelineCategoryFactory: TimelineCategoryFactoryService,
    private dataEntryService: DataEntriesControllerService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------
  @Selector([TimelineState])
  static transitoryTimelineCategories(
    state: TimelineStateModel,
  ): TimelineCategoryPresenterModel[] {
    return state.transitoryTimelineCategories;
  }

  @Selector([TimelineState])
  static transitoryPosts(state: TimelineStateModel): DataEntryPresenterModel[] {
    return state.transitoryPosts;
  }

  @Selector([TimelineState])
  static transitoryTotalCount(state: TimelineStateModel): number {
    return state.transitoryTotalCount;
  }

  @Selector([TimelineState])
  static presentedPosts(state: TimelineStateModel): DataEntryPresenterModel[] {
    return state.presentedPosts;
  }

  @Selector([TimelineState])
  static totalCount(state: TimelineStateModel): number {
    return state.page.totalCount;
  }

  @Selector([TimelineState])
  static activeCount(state: TimelineStateModel): number {
    return state.activeCount;
  }

  static loading(type: LoadingType) {
    return createSelector(
      [TimelineState],
      (state: TimelineStateModel) => state.loading[type],
    );
  }

  static postActionLoading(id) {
    return createSelector(
      [TimelineState],
      (state: TimelineStateModel) => state.postActionsLoaders[id],
    );
  }

  @Selector([TimelineState])
  static selectedPost(state: TimelineStateModel): DataEntryPresenterModel {
    return state.selectedPost;
  }

  @Action(TimelineActions.TransitoryFetchTimelinePosts)
  transitoryFetchTimelinePosts(
    { patchState, getState }: StateContext<TimelineStateModel>,
    { payload }: TimelineActions.TransitoryFetchTimelinePosts,
  ): Observable<any> {
    const appliedFilter = getState().appliedFilter;

    const filter = this.preparePostsFilter(appliedFilter, {
      ...payload,
      orderByPublishPosts: true,
    } as TimelineAppliedFilter);

    return forkJoin([
      this.timelineService.getPosts({
        filter,
        ...(!!payload.viewId && {
          context: new ManagedHttpContext({ publicAccessId: payload.viewId }),
        }),
      }),
      this.timelineService.getPostsCount({
        where: this.apiHelper.prepareWhere(
          this.prepareEntryCondition(payload as TimelineAppliedFilter),
        ),
        ...(!!payload.viewId && {
          context: new ManagedHttpContext({ publicAccessId: payload.viewId }),
        }),
      }),
    ]).pipe(
      tap(([entries, totalCount]) => {
        const presentedPosts = entries.map((entry) => {
          // Todo remove this when backend return the right specificationVersion
          // Currently it's 0
          entry.specificationVersion = !entry.specificationVersion
            ? 1
            : entry.specificationVersion;
          return this.dataEntryFactoryService.createPresenterModel(
            entry as DataEntry,
          );
        });
        patchState({
          transitoryPosts: presentedPosts,
          transitoryTotalCount: totalCount.count,
        });
      }),
    );
  }

  @Action(TimelineActions.FetchTimelinePosts)
  fetchTimelinePosts(
    { setState, getState, dispatch }: StateContext<TimelineStateModel>,
    { payload }: TimelineActions.FetchTimelinePosts,
  ): Observable<any> {
    const { entityIds, moduleIds, skip, limit, replace } = payload;
    // Start loading
    if (replace) {
      dispatch(new LoadingActions.SetLoading(true));
    }

    setState(
      patch<TimelineStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    // Prepare old results
    const oldpresentedPosts = replace ? [] : getState().presentedPosts;
    const oldDataEntries = replace ? [] : getState().page.items;

    // Prepare filters
    const appliedFilter = getState().appliedFilter;

    const filter = this.preparePostsFilter(appliedFilter, {
      ...payload,
      displayPinnedPosts: true,
    } as TimelineAppliedFilter);

    // Fetch posts
    return forkJoin([
      this.timelineService.getPosts({
        filter,
      }),
      this.timelineService.getPostsCount({
        where: this.apiHelper.prepareWhere(
          this.prepareEntryCondition(payload as TimelineAppliedFilter),
        ),
      }),
      this.timelineService.getPostsCount({
        where: this.apiHelper.prepareWhere(
          this.prepareEntryCondition(payload as TimelineAppliedFilter),
        ),
      }),
    ]).pipe(
      tap(([entries, activeCount, totalCount]) => {
        const presentedItems = entries.map((entry) => {
          // Todo remove this when backend return the right specificationVersion
          // Currently it's 0
          entry.specificationVersion = !entry.specificationVersion
            ? 1
            : entry.specificationVersion;
          const post = this.dataEntryFactoryService.createPresenterModel(
            entry as DataEntry,
          );
          return {
            ...post,
            value: {
              ...post.value,
              publishInfo: !post.value?.publishInfo
                ? {}
                : post.value.publishInfo,
              unpublishInfo: !post.value?.unpublishInfo
                ? {}
                : post.value.unpublishInfo,
              // This is for backwards compatibility, when there is no publishState, this is old post
              // we will convert this to manual true
              timelineConfig: {
                ...post.value.timelineConfig,
                manual: !post.value.publishState
                  ? true
                  : post.value.timelineConfig.manual,
              },
              publishState: {
                ...(!!post.value.publishState && post.value.publishState),
                scheduled: !post.value.publishState.scheduled
                  ? {}
                  : post.value.publishState.scheduled,
              },
            },
          } as TimelinePostPresenterModel;
        });
        setState(
          patch<TimelineStateModel>({
            presentedPosts: oldpresentedPosts.concat(presentedItems),
            page: {
              items: oldDataEntries.concat(oldDataEntries),
              totalCount: totalCount?.count || 0,
            },
            appliedFilter: patch({
              skip: skip ?? appliedFilter.skip,
              limit: limit ?? appliedFilter.limit,
              moduleIds: moduleIds ?? appliedFilter.moduleIds,
              entityIds: entityIds ?? appliedFilter.entityIds,
            }),
            activeCount: activeCount.count,
          }),
        );
      }),
      finalize(() => {
        if (replace) {
          dispatch(new LoadingActions.SetLoading(false));
        }
        setState(
          patch<TimelineStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  preparePostsFilter(
    appliedFilter: TimelineAppliedFilter,
    newlyAppliedFilter: TimelineAppliedFilter,
  ): { filter: string } {
    return this.apiHelper.prepareFilter<DataEntry>({
      where: this.prepareEntryCondition(newlyAppliedFilter),
      skip: newlyAppliedFilter.skip ?? appliedFilter.skip,
      limit: newlyAppliedFilter.limit ?? appliedFilter.limit,
      order: this.constructFilterOrder(newlyAppliedFilter),
    });
  }

  prepareEntryCondition(
    newlyAppliedFilter: TimelineAppliedFilter,
  ): Where<DataEntry> {
    return {
      state: DataEntryStateEnum.Active,
      ...(newlyAppliedFilter.entityIds && {
        'value.reference.dataEntity': {
          inq: Array.isArray(newlyAppliedFilter.entityIds)
            ? newlyAppliedFilter.entityIds
            : [newlyAppliedFilter.entityIds],
        },
      }),
      ...(newlyAppliedFilter.moduleIds && {
        'value.module.dataEntry': {
          inq: Array.isArray(newlyAppliedFilter.moduleIds)
            ? newlyAppliedFilter.moduleIds
            : [newlyAppliedFilter.moduleIds],
        },
      }),
      ...(!!newlyAppliedFilter.template && {
        'value.timelineConfig.category.dataEntry':
          newlyAppliedFilter.template?.dataEntry,
      }),
    };
  }

  @Action(TimelineActions.FetchTimelinePostById)
  fetchTimelinePostById(
    { setState }: StateContext<TimelineStateModel>,
    { payload }: TimelineActions.FetchTimelinePostById,
  ): Observable<any> {
    this._setLoadingState(setState, true);
    return this.timelineService
      .getPostsById({
        id: payload.id,
      })
      .pipe(
        map((post) =>
          this.dataEntryFactoryService.createPresenterModel({
            ...post,
            specificationVersion: post?.specificationVersion ?? 1,
          } as DataEntry),
        ),
        tap((post) => {
          setState(
            patch<TimelineStateModel>({
              selectedPost: post as TimelinePostPresenterModel,
            }),
          );
        }),
        finalize(() => {
          this._setLoadingState(setState, false);
        }),
      );
  }

  @Action(TimelineActions.PinPost)
  pinPost(
    { setState, getState, dispatch }: StateContext<TimelineStateModel>,
    { payload }: TimelineActions.PinPost,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<TimelineStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );
    return this.timelineService
      .pinnedPost({
        id: payload.id,
        body: {
          pinned: payload.isPinned,
        },
      })
      .pipe(
        tap(() => {
          const posts = getState().presentedPosts;
          const post = posts.find((p) => p.id === payload.id);
          if (post) {
            setState(
              patch<TimelineStateModel>({
                presentedPosts: updateItem(
                  (i) => i.id === payload.id,
                  patch({
                    value: patch({
                      pinned: payload.isPinned,
                    }),
                  }),
                ),
              }),
            );
          }
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<TimelineStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(TimelineActions.PublishPost)
  publishPost(
    { setState, getState, dispatch }: StateContext<TimelineStateModel>,
    { payload }: TimelineActions.PublishPost,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    const postLoaders = getState().postActionsLoaders[payload.id]
      ? {
          ...getState().postActionsLoaders[payload.id],
          isPublishLoading: true,
        }
      : {
          isPublishLoading: true,
        };
    setState(
      patch<TimelineStateModel>({
        postActionsLoaders: patch<PostActionLoaderItem>({
          [payload.id]: postLoaders,
        }),
      }),
    );
    return this.timelineService
      .publishPost({
        id: payload.id,
        body: {
          publish: payload.isPublished,
        },
      })
      .pipe(
        switchMap(() => this.timelineService.getPostsById({ id: payload.id })),
        tap((post: DataEntry) => {
          const presenter =
            this.dataEntryFactoryService.createPresenterModel(post);
          if (presenter) {
            setState(
              patch<TimelineStateModel>({
                presentedPosts: updateItem<TimelinePostPresenterModel>(
                  (i) => i.id === payload.id,
                  presenter as TimelinePostPresenterModel,
                ),
              }),
            );
          }
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<TimelineStateModel>({
              postActionsLoaders: patch<PostActionLoaderItem>({
                [payload.id]: patch<PostActionLoader>({
                  isPublishLoading: false,
                }),
              }),
            }),
          );
        }),
      );
  }

  @Action(TimelineActions.PublishPostByDate)
  publishPostByDate(
    { setState, getState, dispatch }: StateContext<TimelineStateModel>,
    { payload }: TimelineActions.PublishPostByDate,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    const postLoaders = getState().postActionsLoaders[payload.id]
      ? {
          ...getState().postActionsLoaders[payload.id],
          isPublishDateLoading: true,
        }
      : {
          isPublishDateLoading: true,
        };
    setState(
      patch<TimelineStateModel>({
        postActionsLoaders: patch<PostActionLoaderItem>({
          [payload.id]: postLoaders,
        }),
      }),
    );
    return this.timelineService
      .publishPost({
        id: payload.id,
        body: {
          ...(payload.isPublished && {
            publishDate: payload.date.toISOString(),
          }),
          ...(!payload.isPublished && {
            unpublishDate: payload.date.toISOString(),
          }),
        },
      })
      .pipe(
        switchMap(() => this.timelineService.getPostsById({ id: payload.id })),
        tap((post: DataEntry) => {
          const presenter =
            this.dataEntryFactoryService.createPresenterModel(post);
          if (presenter) {
            setState(
              patch<TimelineStateModel>({
                presentedPosts: updateItem<TimelinePostPresenterModel>(
                  (i) => i.id === payload.id,
                  presenter as TimelinePostPresenterModel,
                ),
              }),
            );
          }
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<TimelineStateModel>({
              postActionsLoaders: patch<PostActionLoaderItem>({
                [payload.id]: patch<PostActionLoader>({
                  isPublishDateLoading: false,
                }),
              }),
            }),
          );
        }),
      );
  }

  private _setLoadingState(
    setState: (
      val: TimelineStateModel | StateOperator<TimelineStateModel>,
    ) => void,
    loading: boolean,
  ): void {
    setState(
      patch<TimelineStateModel>({
        loading: patch<LoadingIndicator>({
          page: loading,
        }),
      }),
    );
  }

  @Action(TimelineActions.TransitoryFetchTimelineCategories)
  transitoryFetchTimelineCategories({
    patchState,
  }: StateContext<TimelineStateModel>): Observable<any> {
    const where: Where<DataEntry> = {
      state: DataEntryStateEnum.Active,
    };

    return this.dataEntryService
      .find({
        entity: TIMELINE_CATEGORY_ENTITY_ID,
        filter: this.apiHelper.prepareFilter<DataEntry>({
          where,
        }),
      })
      .pipe(
        // Make parser here
        tap((entries) => {
          const presentedPosts = entries.map((entry) =>
            this.timelineCategoryFactory.createPresenterModel(entry),
          );
          patchState({
            transitoryTimelineCategories: presentedPosts,
          });
        }),
      );
  }

  private constructFilterOrder(
    newlyAppliedFilter: TimelineAppliedFilter,
  ): string[] {
    const order = [];
    if (newlyAppliedFilter.displayPinnedPosts) {
      order.push('value.pinned DESC');
    }
    if (newlyAppliedFilter.orderByPublishPosts) {
      order.push('value.publishInfo.doneAt DESC');
    }
    order.push('creationInfo.doneAt DESC');
    return order;
  }
}
